import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../firebase-config";

const AuthContext = createContext(null);

const clearStoredSession = () => {
  if (typeof window === "undefined") return;
  ["isLoggedIn", "loggedInKey", "loggedInUser", "userRole", "userGroupId"].forEach(
    (key) => localStorage.removeItem(key)
  );
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return undefined;
    }

    return onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        clearStoredSession();
        setUser(null);
        setProfile(null);
        setLoading(false);
        return;
      }

      setUser(firebaseUser);
      try {
        const snapshot = await getDoc(doc(db, "users", firebaseUser.uid));
        const userProfile = snapshot.exists() ? snapshot.data() : {};
        if (userProfile.status === "suspended") {
          await signOut(auth);
          clearStoredSession();
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }
        setProfile(userProfile);
        localStorage.setItem("isLoggedIn", "true");
        localStorage.setItem("loggedInKey", firebaseUser.uid);
        // Remove the obsolete identity key so it cannot diverge again.
        localStorage.removeItem("loggedInUser");
        localStorage.setItem("userRole", userProfile.role || "member");
        localStorage.setItem("userGroupId", userProfile.groupId || "");
      } catch (error) {
        console.error("Unable to load the signed-in member profile:", error);
        setProfile({});
      } finally {
        setLoading(false);
      }
    });
  }, []);

  const logout = async () => {
    if (auth) await signOut(auth);
    clearStoredSession();
    setUser(null);
    setProfile(null);
  };

  const value = useMemo(
    () => ({
      user,
      profile,
      loading,
      isLoggedIn: Boolean(user),
      userId: user?.uid || null,
      role: profile?.role || "",
      groupId: profile?.groupId || "",
      logout,
    }),
    [user, profile, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
